import { useFormik } from "formik";
import { eventNotificationsUpdateReqDtoSchema } from "service";
import {
  EventNotificationsUpdateReqDto,
  EventNotificationsResDto,
} from "service/api/estabrookSchemas";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";
import { useUpdateEventNotification } from "./use-event-notifications";
import { NotificationTypeArray } from "service/types/enum/notifications-enum";
import {
  BaseFormHookProps,
  BaseFormHookReturn,
} from "../../../shared/types/formTypes";

interface UseEventNotificationFormProps extends BaseFormHookProps {
  notificationId: string;
  eventNotificationData?: EventNotificationsResDto & {
    notificationType?: NotificationTypeArray;
  };
}

export const useEventNotificationForm = ({
  notificationId,
  eventNotificationData,
  onSuccess,
}: UseEventNotificationFormProps): BaseFormHookReturn<EventNotificationsUpdateReqDto> => {
  const { mutate, isLoading } = useUpdateEventNotification({
    onSuccess: () => {
      resetForm();
      // Use setTimeout to ensure form state is reset before navigation
      setTimeout(() => {
        onSuccess?.();
      }, 10);
    },
  });

  const formik = useFormik<EventNotificationsUpdateReqDto>({
    initialValues: {
      notificationTitle: eventNotificationData?.notificationTitle || "",
      notificationCustomMessage:
        eventNotificationData?.notificationCustomMessage ||
        eventNotificationData?.notificationDefaultMessage ||
        "",
      notificationType: eventNotificationData?.notificationType || [],
    },
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(
      eventNotificationsUpdateReqDtoSchema,
    ),
    validate: toFormikValidate(eventNotificationsUpdateReqDtoSchema),
    onSubmit: (values) => {
      mutate({
        body: values,
        pathParams: { id: notificationId },
      });
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    resetForm,
  } = formik;

  return {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    resetForm,
    isLoading,
  };
};
